import { InfoCircleFilled } from "@ant-design/icons";
import { AnalyticsEventNameType } from "@coeff/api";
import { COLORS, ERROR_TITLES, WARNING_TITLES } from "@coeff/utils";
import { Tooltip } from "antd";
import clsx from "clsx";
import React, { useContext, useEffect } from "react";
import styled from "styled-components";

import { EventManagerContext } from "../globals/contexts";

import { FlexRow } from "./FlexLayout";
import { Typography } from "./Typography";

const Section = styled.section<{ noMargin?: boolean; noPadding?: boolean }>`
  margin: ${({ noMargin }) => (noMargin ? 0 : "15px")};
  padding: ${({ noPadding }) => (noPadding ? 0 : "10px")};
  background: ${COLORS.orangeBg};
  border-radius: 5px;
`;

const StyledPanel = styled.div`
  .header {
    display: flex;
    line-height: 20px;

    .sectionIcon {
      color: ${COLORS.orange};
      margin-right: 5px;
    }

    .title {
      flex: 1;
      font-size: 14px;
      font-weight: bold;
      padding: 0px 10px;
    }

    &.marginBottom {
      margin-bottom: 8px;
    }
  }

  .body {
    margin: 8px 10px 8px 30px;
    line-height: 18px;

    &.noIcon {
      margin: 8px 10px;
    }

    ul,
    ol {
      padding-left: 20px;
    }
  }

  .action {
    margin: 8px 30px 0px;

    &.noIcon {
      margin: 8px 10px 0px;
    }
    color: ${COLORS.coeblue4};
    font-weight: 500;
  }
`;

export type WarningMessageProps = {
  action?: () => void;
  actionText?: string;
  className?: string;
  message?: React.ReactNode;
  messageHtml?: string;
  title?: React.ReactNode;
  style?: React.CSSProperties;
  noMargin?: boolean;
  noPadding?: boolean;
  noIcon?: boolean;
  isMessageInTooltip?: boolean;
};

export const WarningMessage: React.FC<WarningMessageProps> = ({
  action,
  actionText,
  message,
  messageHtml,
  title,
  noIcon,
  isMessageInTooltip,
  ...props
}) => {
  const eventManager = useContext(EventManagerContext);

  useEffect(() => {
    if (title === WARNING_TITLES.POPUP_BLOCKED) {
      eventManager?.track(AnalyticsEventNameType.AuthorizationWindowPopupBlockedWarningCardShown);
    }
  }, [title]);

  if (messageHtml) {
    // If messageHtml is specified, prefer it to the message string
    message = undefined;
  }

  if (isMessageInTooltip) {
    return (
      <Tooltip
        title={<div style={{ lineHeight: 1.3 }}>{message}</div>}
        getPopupContainer={node => node}
      >
        <Section className="coeff__warning-message" {...props}>
          <StyledPanel>
            <FlexRow style={{ justifyContent: "space-between" }}>
              <Typography fontWeight={700}>{title ?? ERROR_TITLES.GENERIC_ERROR}</Typography>

              <InfoCircleFilled style={{ cursor: "pointer", color: COLORS.orange }} />
            </FlexRow>
          </StyledPanel>
        </Section>
      </Tooltip>
    );
  }

  return (
    <Section className="coeff__warning-message" {...props}>
      <StyledPanel>
        <div
          className={clsx("header", {
            marginBottom: !message && !messageHtml && action && actionText,
            noIcon,
          })}
        >
          {!noIcon && (
            <div className="sectionIcon">
              <InfoCircleFilled />
            </div>
          )}

          <Typography className="title">{title ?? ERROR_TITLES.GENERIC_ERROR}</Typography>
        </div>

        {messageHtml && (
          <div
            className={clsx("body", {
              noIcon,
            })}
            dangerouslySetInnerHTML={{ __html: messageHtml }}
          ></div>
        )}

        {message && (
          <div
            className={clsx("body", {
              noIcon,
            })}
          >
            {message}
          </div>
        )}

        {action && actionText && (
          <a
            onClick={() => action && action()}
            className={clsx("action", {
              noIcon,
            })}
          >
            {actionText}
          </a>
        )}
      </StyledPanel>
    </Section>
  );
};
